import React, { useContext } from 'react';
import Table, { TableHeader } from '../../../Table';
import Page from '../..';
import { setFilterColumn } from '../../../../store/utils';
import Form from '../../../Form';
import SVG from '../../../SVG';
import { ExportContext } from '../../../../provider/ExportProvider';
import { formatBRL } from '../../../../store/masks';
import { useAbility } from '../../../Hooks/useAbility';

export default function PageExportsTaxes() {
  const {
    taxes,
    checkTaxIds,
    allTaxChecks,
    taxCheckAll,
    checkTaxId,
    deleteTaxLoadings,
    editTax,
    searchFilter,
    setSearchFilter,
    getFilterField,
    getSortField,
    setSortField,
    getAutocompleteItems,
    setAutocompleteItems,
    setField,
    onLineClickHandler,
    filterTax,
    setInvoice,
    TableControl,
    setModal,
  } = useContext(ExportContext)!.controllers.tax;
  const { Can, ability } = useAbility();
  const canDeleteTax = ability?.can('delete', 'Tax');
  const canUpdateTax = ability?.can('update', 'Tax');
  const canUpdateOrDeleteTax = canDeleteTax || canUpdateTax;
  const canUpdateAndDeleteTax = canDeleteTax && canUpdateTax;

  const items = taxes.map((tax, index) => ({
    $isSelected: checkTaxIds.includes(tax.id),
    check: () => (
      <Form.Input.Checkbox
        checked={checkTaxIds.includes(tax.id)}
        onChange={() => checkTaxId(tax.id)}
      />
    ),
    ...tax,
    ...(() =>
      canUpdateOrDeleteTax
        ? {
            control: () => (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '10px',
                }}
              >
                <Can I="update" a="Tax">
                  {() => (
                    <label
                      style={{
                        display: 'flex',
                        cursor: 'pointer',
                        alignItems: 'center',
                      }}
                    >
                      {true ? (
                        <SVG.Icon.Edit
                          onClick={() => {
                            editTax(tax);
                          }}
                          width={25}
                          height={25}
                        />
                      ) : (
                        <SVG.Icon.Loader width={25} height={25} />
                      )}
                    </label>
                  )}
                </Can>
                <Can I="delete" a="Tax">
                  {() => (
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      {!deleteTaxLoadings[index] ? (
                        <SVG.Icon.Trash
                          style={{ padding: '0 2.5px' }}
                          onClick={() => {
                            setModal('single.deleteTaxes', true);
                            setInvoice('selected.tax', tax.id);
                          }}
                          width={20}
                          height={20}
                        />
                      ) : (
                        <SVG.Icon.Loader width={25} height={25} />
                      )}
                    </label>
                  )}
                </Can>
              </label>
            ),
          }
        : {})(),
  }));
  const headers: TableHeader[] = [
    {
      text: (
        <Form.Input.Checkbox checked={allTaxChecks} onChange={taxCheckAll} />
      ),
      value: 'check',
      width: 'auto',
    },
    {
      text: 'Nome',
      value: 'name',
      width: '1fr',
      sort: {
        value: getSortField('name'),
        onChange: setSortField('name'),
      },
      filter: {
        value: getFilterField('name'),
        onChange: setField('name'),
        items: getAutocompleteItems('name'),
        handleItems: () => setAutocompleteItems('name'),
        label: 'Nome da taxa',
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('name', '', searchFilter));
        setInvoice(`autocompletes.taxes.name.filter.value`, '');
      },
    },
    {
      text: 'Description',
      value: 'description',
      width: '1fr',
      sort: {
        value: getSortField('description'),
        onChange: setSortField('description'),
      },
      filter: {
        value: getFilterField('description'),
        onChange: setField('description'),
        items: getAutocompleteItems('description'),
        handleItems: () => setAutocompleteItems('description'),
        label: 'Descrição da taxa',
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('description', '', searchFilter));
        setInvoice(`autocompletes.taxes.description.filter.value`, '');
      },
    },
    {
      text: 'Valor',
      value: 'value',
      width: '1fr',
      mask: ({ value }) => formatBRL(value),
      sort: {
        value: getSortField('value'),
        onChange: setSortField('value'),
      },
    },
    {
      text: 'Criado em',
      value: 'createdAt',
      width: '1fr',
      mask: ({ createdAt }) => new Date(createdAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('createdAt'),
        onChange: setSortField('createdAt'),
      },
      interval: [
        {
          value: getFilterField('createdAt', 'GEQ'),
          onChange: setField('createdAt', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('createdAt', 'LT'),
          onChange: setField('createdAt', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('createdAt', '', searchFilter, 'GEQ');
        filter = setFilterColumn('createdAt', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
    {
      text: 'Atualizado em',
      value: 'updatedAt',
      width: '1fr',
      mask: ({ updatedAt }) => new Date(updatedAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('updatedAt'),
        onChange: setSortField('updatedAt'),
      },
      tooltip: {
        translateX: 1,
      },
      interval: [
        {
          value: getFilterField('updatedAt', 'GEQ'),
          onChange: setField('updatedAt', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('updatedAt', 'LT'),
          onChange: setField('updatedAt', 'LT'),
          label: 'Data final',
        },
      ],
      onClear: () => {
        let filter = setFilterColumn('updatedAt', '', searchFilter, 'GEQ');
        filter = setFilterColumn('updatedAt', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
    ...(() =>
      canUpdateOrDeleteTax
        ? [
            {
              text: '',
              value: 'control',
              width: canUpdateAndDeleteTax ? '80px' : '40px',
              style: {
                margin: 'auto 10px auto auto',
              },
            },
          ]
        : [])(),
  ];

  React.useEffect(() => {
    filterTax();
  }, []);

  return (
    <Page className="page-banner">
      <div
        style={{
          position: 'relative',
          transition: 'min-width 500ms, max-width 500ms',
          width: '100%',
          minHeight: 'calc(100vh - 130px)',
          maxHeight: 'calc(100vh - 130px)',
          margin: '0 1rem',
          background: '#DDDDDD',
          borderRadius: '5px',
          overflow: 'hidden',
        }}
      >
        <Table
          headers={headers}
          items={items}
          size="small"
          onLineClick={onLineClickHandler}
          containerStyle={{ height: 'calc(100vh - 210px)' }}
        >
          <TableControl />
        </Table>
      </div>
    </Page>
  );
}
//
